import { Controller } from "@hotwired/stimulus"
import { mentionChipFor } from "../utils/mention_chips"
import { hostTaskPane, taskPaneTrigger } from "../utils/task_pane"

// Mind map view for a task list. The tree itself is plain server-rendered
// HTML/CSS (nested flexbox — see _mindmap.html.slim / _mindmap_node.html.slim
// / _tasks_mindmap.scss); this controller only adds the pan/zoom chrome and
// the client-side (never persisted) collapse toggle. Pan/zoom follows the
// same shape as contact_tree_controller.js — pointer drag with a movement
// threshold that suppresses the following click, ctrl/cmd+wheel zoom, +/-
// and a reset button — but simpler: no pinch-zoom, no server fetch, since
// the tree is already in the DOM from the server render.
const MIN_SCALE = 0.4
const MAX_SCALE = 1.5
const ZOOM_STEP = 1.2
const PAN_THRESHOLD = 4

export default class extends Controller {
    static targets = ["viewport", "stage"]

    connect() {
        this.scale = 1
        this.tx = 0
        this.ty = 0
        this._panMoved = false
        this._suppressClick = false
        this._pointerId = null
        this._boundPointerMove = (event) => this._onPointerMove(event)
        this._boundPointerUp = (event) => this._onPointerUp(event)

        if (this.hasViewportTarget) {
            this.viewportTarget.addEventListener("wheel", (event) => this._onWheel(event), { passive: false })
            this.viewportTarget.addEventListener("pointerdown", (event) => this._onPointerDown(event))
            this.viewportTarget.addEventListener("dragstart", (event) => event.preventDefault())
            // A drag that started on the canvas background must not also fire
            // whatever the pointer lands on when released (there's nothing
            // clickable on the bare canvas today, but this mirrors
            // contact-tree's guard in case that changes).
            this.viewportTarget.addEventListener("click", (event) => {
                if (!this._suppressClick) return
                this._suppressClick = false
                event.preventDefault()
                event.stopPropagation()
            }, true)
        }

        // Root visible, left-aligned, vertically centered — wait a frame so
        // the flexbox tree has actually laid out before measuring it.
        requestAnimationFrame(() => this._resetView())

        // The task pane opens in this canvas's sidebar (detail-modal); a task
        // it completes is ticked on its node.
        this._unhostPane = hostTaskPane(this, {
            onCompleted: (id, completed) => {
                const box = this.element.querySelector(`[data-mindmap-node][data-task-id="${CSS.escape(id)}"] > .tasks-mindmap-row > [data-mindmap-box]`)
                box?.classList.toggle("is-completed", completed)
            }
        })
    }

    disconnect() {
        this._unhostPane?.()
        window.removeEventListener("pointermove", this._boundPointerMove)
        window.removeEventListener("pointerup", this._boundPointerUp)
        window.removeEventListener("pointercancel", this._boundPointerUp)
    }

    // --- Toolbar ---

    zoomIn(event) {
        event?.preventDefault()
        this._zoomAtViewportCenter(this.scale * ZOOM_STEP)
    }

    zoomOut(event) {
        event?.preventDefault()
        this._zoomAtViewportCenter(this.scale / ZOOM_STEP)
    }

    reset(event) {
        event?.preventDefault()
        this._resetView()
    }

    // Collapse dot AND the node's own box (clicking the box used to deep-link
    // to the list view, which read as a confusing surprise-navigation — see
    // _mindmap_node.html.slim) both toggle the same class that hides the
    // children column, purely client-side, unlike the outline's caret (which
    // POSTs collapsed state to the server). This view is read/navigate only,
    // so nothing here is persisted. A leaf node's box has no children to
    // toggle, so a click there just pulses the box as a "nothing to expand
    // here" acknowledgement instead of silently doing nothing.
    toggleCollapse(event) {
        // A person or company chip opens its card in the side panel
        // (detail-modal, on the canvas element) instead of folding the node.
        const chip = event.type === "click" ? mentionChipFor(event) : null
        if (chip) {
            event.preventDefault()
            event.stopPropagation()
            this.dispatch("open-detail", { detail: { trigger: chip } })
            return
        }
        // A link in a node's title opens itself instead of folding the node.
        if (event.type === "click" && event.target.closest?.(".tasks-mindmap-title a[href]")) {
            event.stopPropagation()
            return
        }
        event.preventDefault()
        event.stopPropagation()
        const node = event.currentTarget.closest("[data-mindmap-node]")
        if (!node) return
        const children = node.querySelector(":scope > [data-mindmap-children]")
        // The "Open task" icon and the note/comment/file badges open the task
        // pane at their section; a leaf's box, with nothing to fold, opens it
        // on the details.
        const paneTarget = event.target.closest?.("[data-pane-section]")
        if (paneTarget || (!children && event.currentTarget.matches("[data-mindmap-box]"))) {
            const section = paneTarget?.dataset.paneSection || "details"
            this.dispatch("open-detail", { detail: { trigger: taskPaneTrigger(node.dataset.taskId, section) } })
            return
        }
        if (!children) {
            this._pulseLeaf(node)
            return
        }
        const collapsed = node.dataset.collapsed !== "true"
        node.dataset.collapsed = collapsed ? "true" : "false"
        children.classList.toggle("is-collapsed", collapsed)
        // Keep both the box (role="button") and the dedicated dot button in
        // sync, regardless of which one was actually clicked.
        node.querySelectorAll(":scope > .tasks-mindmap-row > [aria-expanded]").forEach((el) => {
            el.setAttribute("aria-expanded", collapsed ? "false" : "true")
        })
    }

    // The box's own click toggles collapse (see toggleCollapse above); the
    // "open in list" icon lives inside the box, so its click must not also
    // bubble into that handler. No preventDefault — the anchor still navigates.
    openInList(event) {
        event.stopPropagation()
    }

    _pulseLeaf(node) {
        const box = node.querySelector(":scope > .tasks-mindmap-row > [data-mindmap-box]")
        if (!box) return
        box.classList.remove("tasks-mindmap-box--pulse")
        // Force reflow so a second click while the first pulse is still
        // running restarts the animation instead of being a no-op.
        void box.offsetWidth
        box.classList.add("tasks-mindmap-box--pulse")
    }

    // --- Positioning ---

    _resetView() {
        if (!this.hasViewportTarget || !this.hasStageTarget) return
        this.scale = 1
        const vh = this.viewportTarget.clientHeight
        const sh = this.stageTarget.offsetHeight
        this.tx = 0
        this.ty = Math.max((vh - sh) / 2, 0)
        this._applyTransform()
    }

    _applyTransform() {
        this.stageTarget.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`
    }

    _zoomAtViewportCenter(nextScale) {
        if (!this.hasViewportTarget) return
        const rect = this.viewportTarget.getBoundingClientRect()
        this._zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, nextScale)
    }

    // Zoom keeping the given client point fixed on screen.
    _zoomAt(clientX, clientY, nextScale) {
        nextScale = Math.min(Math.max(nextScale, MIN_SCALE), MAX_SCALE)
        const rect = this.viewportTarget.getBoundingClientRect()
        const px = clientX - rect.left
        const py = clientY - rect.top
        this.tx = px - ((px - this.tx) / this.scale) * nextScale
        this.ty = py - ((py - this.ty) / this.scale) * nextScale
        this.scale = nextScale
        this._applyTransform()
    }

    _onWheel(event) {
        if (!event.ctrlKey && !event.metaKey) return
        event.preventDefault()
        const factor = Math.exp(-event.deltaY * 0.01)
        this._zoomAt(event.clientX, event.clientY, this.scale * factor)
    }

    // --- Pan ---

    _onPointerDown(event) {
        if (event.pointerType === "mouse" && event.button !== 0) return
        // Let node links and the collapse dot handle their own clicks —
        // panning only starts when the drag begins on empty canvas.
        if (event.target.closest(".tasks-mindmap-box, [data-mindmap-toggle]")) return
        this._pointerId = event.pointerId
        this._last = { x: event.clientX, y: event.clientY }
        this._panMoved = false
        window.addEventListener("pointermove", this._boundPointerMove)
        window.addEventListener("pointerup", this._boundPointerUp)
        window.addEventListener("pointercancel", this._boundPointerUp)
    }

    _onPointerMove(event) {
        if (event.pointerId !== this._pointerId || !this._last) return
        const dx = event.clientX - this._last.x
        const dy = event.clientY - this._last.y
        if (!this._panMoved && Math.hypot(dx, dy) < PAN_THRESHOLD) return
        this._panMoved = true
        this.viewportTarget.classList.add("is-panning")
        this._last = { x: event.clientX, y: event.clientY }
        this.tx += dx
        this.ty += dy
        this._applyTransform()
    }

    _onPointerUp(event) {
        if (event.pointerId !== this._pointerId) return
        if (this.hasViewportTarget) this.viewportTarget.classList.remove("is-panning")
        if (this._panMoved) this._suppressClick = true
        this._panMoved = false
        this._last = null
        this._pointerId = null
        window.removeEventListener("pointermove", this._boundPointerMove)
        window.removeEventListener("pointerup", this._boundPointerUp)
        window.removeEventListener("pointercancel", this._boundPointerUp)
    }
}
